import React from "react";
import SeoHead from "@/components/brand/SeoHead";
import GlowingGridBackground from "@/components/brand/GlowingGridBackground";
import GlassCard from "@/components/brand/GlassCard";
import FadeUp from "@/components/brand/FadeUp";
import {
    FormField,
    FormSelect,
    FormTextarea,
    FormCheckboxGroup,
    FormConsentCheckbox,
    HoneypotField,
    FormSubmitButton,
    FormErrorBanner,
    FormSuccessState,
} from "@/components/forms/FormControls";
import {
    useFormSubmit,
    isNonEmpty,
    isEmail,
    isPhone,
    minLength,
} from "@/components/forms/useFormSubmit";

const GRADE_OPTIONS = ["9th", "10th", "11th", "12th"];
const CITY_OPTIONS = [
    "North Fort Myers",
    "Cape Coral",
    "Fort Myers",
    "Lehigh Acres",
    "Other Lee County area",
];
const INTERESTS = [
    "Building a website",
    "Art/design",
    "AI tools",
    "Starting a business someday",
    "Learning digital skills",
    "Not sure yet",
];

const CONSENT =
    "I understand that parent/guardian approval is required before participation.";

const initialValues = {
    studentFirstName: "",
    studentLastInitial: "",
    gradeLevel: "",
    cityArea: "",
    studentInterests: [],
    whyInterested: "",
    parentName: "",
    parentEmail: "",
    parentPhone: "",
    consentAccepted: false,
    hp_company_website: "",
};

const validate = (v) => {
    const e = {};
    if (!isNonEmpty(v.studentFirstName))
        e.studentFirstName = "This field is required.";
    if (!isNonEmpty(v.studentLastInitial))
        e.studentLastInitial = "This field is required.";
    else if (v.studentLastInitial.trim().length > 3)
        e.studentLastInitial = "Please enter 1–3 characters.";
    if (!isNonEmpty(v.gradeLevel))
        e.gradeLevel = "Please choose a grade level.";
    if (!isNonEmpty(v.cityArea))
        e.cityArea = "Please choose an area.";
    if (!v.studentInterests || v.studentInterests.length === 0)
        e.studentInterests = "Please select at least one option.";
    if (!minLength(v.whyInterested, 10))
        e.whyInterested = "Please write at least 10 characters.";
    if (!isNonEmpty(v.parentName))
        e.parentName = "This field is required.";
    if (!isEmail(v.parentEmail))
        e.parentEmail = "Please enter a valid email address.";
    if (!isPhone(v.parentPhone))
        e.parentPhone =
            "Please enter a valid phone number (digits, spaces, +, -, ()).";
    if (!v.consentAccepted)
        e.consentAccepted = "Please confirm to continue.";
    return e;
};

export default function StudentInterest() {
    const form = useFormSubmit({
        formType: "student_interest",
        initialValues,
        validate,
    });

    return (
        <>
            <SeoHead
                title="Student Interest | Future Builders Lab"
                description="Tell us a little about yourself. Future Builders Lab is a 6-week creative pilot for Lee County high school students. Parent or guardian approval is required."
                path="/student-interest"
            />

            {/* Hero */}
            <section
                className="vfs-section relative overflow-hidden"
                style={{ paddingTop: "clamp(2rem, 6vw, 4rem)" }}
                data-testid="student-hero"
            >
                <GlowingGridBackground accent="#3B82F6" opacity={0.18} />
                <div className="vfs-container relative">
                    <div className="max-w-3xl">
                        <FadeUp>
                            <span className="vfs-eyebrow">Student Interest</span>
                            <h1 className="vfs-h1 mt-5" data-testid="student-h1">
                                Interested in{" "}
                                <span className="vfs-grad-text">
                                    Future Builders Lab?
                                </span>
                            </h1>
                            <p
                                className="mt-6 text-lg leading-relaxed"
                                style={{ color: "var(--text-secondary)" }}
                            >
                                Tell us a little about yourself and why you are
                                interested. Parent or guardian approval is
                                required before participation.
                            </p>
                        </FadeUp>
                    </div>
                </div>
            </section>

            {/* Intro */}
            <section className="vfs-section" style={{ paddingTop: 0 }}>
                <div className="vfs-container">
                    <div className="max-w-3xl">
                        <FadeUp>
                            <p
                                className="text-lg leading-relaxed"
                                style={{ color: "var(--text-secondary)" }}
                            >
                                Future Builders Lab is designed for high school
                                students who want to build something real,
                                learn beginner-friendly digital skills, and
                                practice using AI-assisted tools safely.
                            </p>
                        </FadeUp>
                    </div>
                </div>
            </section>

            {/* Safety reassurance */}
            <section className="vfs-section" style={{ paddingTop: 0 }}>
                <div className="vfs-container">
                    <FadeUp>
                        <div
                            className="vfs-glass p-6 sm:p-7 max-w-3xl"
                            style={{
                                borderRadius: 18,
                                borderColor: "rgba(20, 184, 166, 0.35)",
                            }}
                            data-testid="student-safety-note"
                        >
                            <p
                                className="text-base leading-relaxed"
                                style={{ color: "var(--text-primary)" }}
                            >
                                We only ask for your first name and last
                                initial — you do not need to share a full last
                                name. Your information is private and used only
                                to follow up about the pilot. Parent or
                                guardian approval is required before you can
                                participate.
                            </p>
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* Form */}
            <section
                id="student-form"
                className="vfs-section"
                style={{ paddingTop: 0, scrollMarginTop: "6rem" }}
                data-testid="student-form-section"
            >
                <div className="vfs-container">
                    <FadeUp>
                        <GlassCard
                            className="p-6 sm:p-10 max-w-3xl mx-auto"
                            hoverLift={false}
                        >
                            {form.success ? (
                                <FormSuccessState
                                    heading="Thanks for letting us know you're interested."
                                    body={"We'll reach out to your parent or guardian to confirm next steps. Future Builders Lab pilot dates will be announced once finalized."}
                                    onReset={form.reset}
                                    testId="student-success"
                                />
                            ) : (
                                <form
                                    onSubmit={form.handleSubmit}
                                    noValidate
                                    data-testid="student-form"
                                >
                                    <h2 className="vfs-h2 mb-7">About you</h2>
                                    <FormErrorBanner message={form.serverError} />
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <FormField
                                            id="studentFirstName"
                                            name="studentFirstName"
                                            label="Student first name"
                                            required
                                            maxLength={50}
                                            value={
                                                form.values.studentFirstName
                                            }
                                            onChange={form.handleChange}
                                            onBlur={form.handleBlur}
                                            error={
                                                form.errors.studentFirstName
                                            }
                                            testId="student-first-name"
                                        />
                                        <FormField
                                            id="studentLastInitial"
                                            name="studentLastInitial"
                                            label="Student last initial"
                                            required
                                            maxLength={3}
                                            placeholder="e.g. S"
                                            value={
                                                form.values.studentLastInitial
                                            }
                                            onChange={form.handleChange}
                                            onBlur={form.handleBlur}
                                            error={
                                                form.errors.studentLastInitial
                                            }
                                            testId="student-last-initial"
                                        />
                                        <FormSelect
                                            id="gradeLevel"
                                            name="gradeLevel"
                                            label="Grade level"
                                            required
                                            options={GRADE_OPTIONS}
                                            value={form.values.gradeLevel}
                                            onChange={form.handleChange}
                                            onBlur={form.handleBlur}
                                            error={form.errors.gradeLevel}
                                            testId="student-grade"
                                        />
                                        <FormSelect
                                            id="cityArea"
                                            name="cityArea"
                                            label="City / area"
                                            required
                                            options={CITY_OPTIONS}
                                            value={form.values.cityArea}
                                            onChange={form.handleChange}
                                            onBlur={form.handleBlur}
                                            error={form.errors.cityArea}
                                            testId="student-city"
                                        />
                                    </div>

                                    <div className="mt-7">
                                        <FormCheckboxGroup
                                            name="studentInterests"
                                            legend="What interests you most?"
                                            required
                                            options={INTERESTS}
                                            value={
                                                form.values.studentInterests
                                            }
                                            onChange={form.handleChange}
                                            error={
                                                form.errors.studentInterests
                                            }
                                            testId="student-interests"
                                        />
                                    </div>

                                    <div className="mt-7">
                                        <FormTextarea
                                            id="whyInterested"
                                            name="whyInterested"
                                            label="Why are you interested in Future Builders Lab?"
                                            required
                                            maxLength={1000}
                                            value={form.values.whyInterested}
                                            onChange={form.handleChange}
                                            onBlur={form.handleBlur}
                                            error={form.errors.whyInterested}
                                            testId="student-why"
                                        />
                                    </div>

                                    <h3 className="vfs-h3 mt-10 mb-5">
                                        Parent / guardian contact
                                    </h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <FormField
                                            id="parentName"
                                            name="parentName"
                                            label="Parent/guardian name"
                                            required
                                            autoComplete="name"
                                            maxLength={100}
                                            value={form.values.parentName}
                                            onChange={form.handleChange}
                                            onBlur={form.handleBlur}
                                            error={form.errors.parentName}
                                            testId="student-parent-name"
                                        />
                                        <FormField
                                            id="parentEmail"
                                            name="parentEmail"
                                            type="email"
                                            label="Parent/guardian email"
                                            required
                                            autoComplete="email"
                                            value={form.values.parentEmail}
                                            onChange={form.handleChange}
                                            onBlur={form.handleBlur}
                                            error={form.errors.parentEmail}
                                            testId="student-parent-email"
                                            inputMode="email"
                                        />
                                        <FormField
                                            id="parentPhone"
                                            name="parentPhone"
                                            type="tel"
                                            label="Parent/guardian phone"
                                            required
                                            autoComplete="tel"
                                            value={form.values.parentPhone}
                                            onChange={form.handleChange}
                                            onBlur={form.handleBlur}
                                            error={form.errors.parentPhone}
                                            testId="student-parent-phone"
                                            inputMode="tel"
                                        />
                                    </div>

                                    <div className="mt-7">
                                        <FormConsentCheckbox
                                            id="consentAccepted"
                                            name="consentAccepted"
                                            label={CONSENT}
                                            checked={
                                                form.values.consentAccepted
                                            }
                                            onChange={form.handleChange}
                                            error={
                                                form.errors.consentAccepted
                                            }
                                            testId="student-consent"
                                        />
                                    </div>

                                    <HoneypotField
                                        value={form.values.hp_company_website}
                                        onChange={form.handleChange}
                                    />

                                    <div className="mt-8">
                                        <FormSubmitButton
                                            submitting={form.submitting}
                                            testId="student-submit"
                                        >
                                            Submit Interest Form
                                        </FormSubmitButton>
                                    </div>
                                </form>
                            )}
                        </GlassCard>
                    </FadeUp>
                </div>
            </section>
        </>
    );
}
